import React, { useEffect, useState } from 'react';
import { API } from 'aws-amplify';

import { useUsers } from '../../context/users';
import { listZipcodess } from '../../graphql/queries';
import { createZipcodes, updateZipcodes } from '../../graphql/mutations';
import { FACILITIES, FACILITY_KEYS } from '../../utils/facilities';

const Settings = (): JSX.Element => {
  const { facilityInfo } = useUsers();

  // The admin app is scoped to one facility — only show that facility's region.
  const regionKey = FACILITY_KEYS.find(k => FACILITIES[k].id === facilityInfo?.id) ?? null;

  // region key ('richmond' | 'tacoma') -> textarea text, one zip per line
  const [zipText, setZipText] = useState<Record<string, string>>({});
  // region keys that already have a Zipcodes record (vs. need create)
  const [existing, setExisting] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [savingRegion, setSavingRegion] = useState<string | null>(null);
  const [savedRegion, setSavedRegion] = useState<string | null>(null);

  useEffect(() => {
    const fetchZips = async () => {
      try {
        const res: any = await API.graphql({ query: listZipcodess });
        const items: Array<{ id: string; zipcodes?: string[] }> =
          res?.data?.listZipcodess?.items ?? [];
        const text: Record<string, string> = {};
        const have = new Set<string>();
        items.forEach(item => {
          have.add(item.id);
          text[item.id] = (item.zipcodes ?? []).filter(Boolean).join('\n');
        });
        setExisting(have);
        setZipText(text);
      } catch (e) {
        console.error('Error loading zip codes:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchZips();
  }, []);

  const handleSave = async (region: string) => {
    setSavingRegion(region);
    setSavedRegion(null);
    try {
      // Accept newline- or comma-separated input.
      const zipcodes = (zipText[region] ?? '')
        .split(/[\n,]/)
        .map(z => z.trim())
        .filter(Boolean);
      const mutation = existing.has(region) ? updateZipcodes : createZipcodes;
      await API.graphql({
        query: mutation,
        variables: { input: { id: region, zipcodes } },
      });
      setExisting(prev => new Set(prev).add(region));
      setZipText(prev => ({ ...prev, [region]: zipcodes.join('\n') }));
      setSavedRegion(region);
      setTimeout(() => setSavedRegion(s => (s === region ? null : s)), 3000);
    } catch (e) {
      console.error('Error saving zip codes:', e);
      alert('Failed to save. Please try again.');
    } finally {
      setSavingRegion(null);
    }
  };

  const zipCount = (region: string) =>
    (zipText[region] ?? '').split(/[\n,]/).map(z => z.trim()).filter(Boolean).length;

  return (
    <div style={{ padding: '16px 24px', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: '#111827', marginTop: 0 }}>Settings</h1>

      <section style={{ marginBottom: 28 }}>
        <h2 style={sectionTitle}>Facility</h2>
        <div style={card}>
          <span style={{ fontSize: 14, color: '#111827' }}>{facilityInfo?.name || '—'}</span>
        </div>
      </section>

      <section>
        <h2 style={sectionTitle}>Service area zip codes</h2>
        <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 12px' }}>
          One zip code per line. These define which addresses each region serves.
        </p>

        {loading ? (
          <div style={{ fontSize: 13, color: '#9CA3AF' }}>Loading…</div>
        ) : !regionKey ? (
          <div style={{ fontSize: 13, color: '#9CA3AF' }}>
            This facility isn’t mapped to a service region yet.
          </div>
        ) : (
          <div style={{ ...card, maxWidth: 360 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                {FACILITIES[regionKey].label}
              </span>
              <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                {zipCount(regionKey)} zip{zipCount(regionKey) !== 1 ? 's' : ''}
              </span>
            </div>
            <textarea
              value={zipText[regionKey] ?? ''}
              onChange={e => setZipText(prev => ({ ...prev, [regionKey]: e.target.value }))}
              placeholder={'94801\n94804\n…'}
              rows={10}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid #D1D5DB',
                fontSize: 13,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                resize: 'vertical',
                outline: 'none',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
              <button
                onClick={() => handleSave(regionKey)}
                disabled={savingRegion === regionKey}
                style={{
                  padding: '7px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: savingRegion === regionKey ? '#C084FC' : '#7B1FA2',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: savingRegion === regionKey ? 'not-allowed' : 'pointer',
                }}
              >
                {savingRegion === regionKey ? 'Saving…' : 'Save'}
              </button>
              {savedRegion === regionKey && (
                <span style={{ fontSize: 13, color: '#2e7d32' }}>Saved!</span>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

const sectionTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 10,
};

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  padding: 16,
  boxSizing: 'border-box',
};

export default Settings;
